import { useState } from "react";
import { useNavigate } from "react-router-dom";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Button, CircularProgress } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
} from "recharts";
import { useGetDashboardDataQuery, useGetDashboardGraphDataQuery } from "../../store/api/manageAPI";
import Loader from "../../constants/Loader";

const Dashboard = () => {
  const navigate = useNavigate();

  // Independent state for each chart
  const [ordersTimeRange, setOrdersTimeRange] = useState("year");
  const [revenueTimeRange, setRevenueTimeRange] = useState("year");

  // Fetch Orders Data
  const { data: ordersDataRes, isLoading: isLoadingOrders } = useGetDashboardGraphDataQuery({
    type: "orders",
    period: ordersTimeRange
  });

  // Fetch Revenue Data
  const { data: revenueDataRes, isLoading: isLoadingRevenue } = useGetDashboardGraphDataQuery({
    type: "revenue",
    period: revenueTimeRange
  });

  // Fetch Dashboard Stats Data
  const { data: dashboardDataRes, isLoading: isLoadingDashboard } = useGetDashboardDataQuery();
  const dashboardData = dashboardDataRes?.data;

  // Transform Data for Charts
  const ordersChartData = ordersDataRes?.data?.data?.map((item: any) => ({
    name: item.label,
    orders: item.value
  })) || [];

  const revenueChartData = revenueDataRes?.data?.data?.map((item: any) => ({
    name: item.label,
    revenue: item.value
  })) || [];

  return (
    <>
      <Loader isLoad={isLoadingOrders || isLoadingRevenue || isLoadingDashboard} />
      <div className="main_title">
        <h1>Dashboard</h1>
      </div>

      <div className="stats_boxes gap_m">
        <div className="cards" onClick={() => navigate("/manage-users")}>
          <figure>
            <img src="/static/images/user_icon.svg" alt="Users" />
          </figure>
          <h2>{dashboardData?.totalUsers !== undefined ? dashboardData?.totalUsers : 0}</h2>
          <p>Users</p>
        </div>

        <div className="cards" onClick={() => navigate("/manage-users")}>
          <figure>
            <img src="/static/images/user_icon.svg" alt="Active Users" />
          </figure>
          <h2>{dashboardData?.usersJoinedToday !== undefined ? dashboardData?.usersJoinedToday : 0}</h2>
          <p>New User today</p>
        </div>

        <div className="cards" onClick={() => navigate("/manage-loyality-points")}>
          <figure>
            <img src="/static/images/offer_icon.svg" alt="Revenue" />
          </figure>
          <h2>{dashboardData?.loyaltyPointsIssued !== undefined ? dashboardData?.loyaltyPointsIssued : 0}</h2>
          <p>Loyalty Points Issued</p>
        </div>
        <div className="cards" onClick={() => navigate("/manage-order")}>
          <figure>
            <img src="/static/images/order.svg" alt="Revenue" />
          </figure>
          <h2>{dashboardData?.totalOrders !== undefined ? dashboardData?.totalOrders : 0}</h2>
          <p>Total Order </p>
        </div>
        <div className="cards" onClick={() => navigate("/manage-order")}>
          <figure>
            <img src="/static/images/order.svg" alt="Revenue" />
          </figure>
          <h2>{dashboardData?.ordersToday !== undefined ? dashboardData?.ordersToday : 0}</h2>
          <p>New Order Today</p>
        </div>
        <div className="cards" onClick={() => navigate("/manage-order")}>
          <figure>
            <img src="/static/images/order.svg" alt="Revenue" />
          </figure>
          <h2>{dashboardData?.completedOrders !== undefined ? dashboardData?.completedOrders : 0}</h2>
          <p>Completed Order</p>
        </div>
        <div className="cards" onClick={() => navigate("/manage-flavour")}>
          <figure>
            <img src="/static/images/flavor.svg" alt="Revenue" />
          </figure>
          <h2>{dashboardData?.totalFlavors !== undefined ? dashboardData?.totalFlavors : 0}</h2>
          <p>Total Flavors</p>
        </div>

        <div className="cards" onClick={() => navigate("/manage-toppings")}>
          <figure>
            <img src="/static/images/topping.svg" alt="Revenue" />
          </figure>
          <h2>{dashboardData?.totalToppings !== undefined ? dashboardData?.totalToppings : 0}</h2>
          <p>Total Toppings Category</p>
        </div>

      </div>

      <div className="grid_sc gap_m">
        {/* Total Orders Chart */}
        <div className="cards table_card">
          <div className="cards_header">
            <div className="left_s">
              <h2>Total Order</h2>
            </div>
            <div className="right_s">
              <p>
                {['day', 'week', 'month', 'year'].map((range) => (
                  <span
                    key={range}
                    className={ordersTimeRange === range ? "active" : ""}
                    onClick={() => setOrdersTimeRange(range)}
                  >
                    A {range}
                  </span>
                ))}
              </p>
              <Button className="btn btn_black icon_btn"><FileDownloadIcon /> </Button>
            </div>
          </div>
          <div style={{ width: '100%', height: 300 }}>
            {isLoadingOrders ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <CircularProgress />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ordersChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="orders" fill="#38B6FF" barSize={40} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Total Revenue Chart */}
        <div className="cards table_card">
          <div className="cards_header">
            <div className="left_s">
              <h2>Total Revenue</h2>
            </div>
            <div className="right_s">
              <p>
                {['day', 'week', 'month', 'year'].map((range) => (
                  <span
                    key={range}
                    className={revenueTimeRange === range ? "active" : ""}
                    onClick={() => setRevenueTimeRange(range)}
                  >
                    A {range}
                  </span>
                ))}
              </p>
              <Button className="btn btn_black icon_btn"><FileDownloadIcon /> </Button>
            </div>
          </div>
          <div style={{ width: '100%', height: 300 }}>
            {isLoadingRevenue ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <CircularProgress />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="revenue" fill="#38B6FF" barSize={40} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

      </div>

    </>
  );
};

export default Dashboard;